'use client';

import * as React from 'react';
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'wheel'] as const;

/** Cross-tab sync key: latest activity timestamp shared across tabs. */
const ACTIVITY_STORAGE_KEY = 'cntxt.session.lastActivity';
/** Cross-tab sync key: bumped when any tab chooses "stay signed in". */
const STAY_ACTIVE_STORAGE_KEY = 'cntxt.session.stayActive';

/** Heartbeat granularity for evaluating idle/countdown state. */
const TICK_INTERVAL_MS = 1000;
/** Throttle cross-tab activity writes to avoid storage spam. */
const ACTIVITY_WRITE_THROTTLE_MS = 1000;
export type UseIdleTimeoutOptions = {
  /** Inactivity before the keep-alive prompt appears. */
  idleTimeoutMs: number;
  /** How long the prompt stays open before auto sign-out is triggered. */
  warningCountdownMs: number;
  /** Disable all tracking (e.g. when unauthenticated). Defaults to true. */
  enabled?: boolean;
  /** Invoked once when the countdown elapses without the user staying active. */
  onIdleTimeout: () => void;
};
export type UseIdleTimeoutResult = {
  /** Whether the keep-alive prompt should be shown. */
  isPrompted: boolean;
  /** Milliseconds left on the warning countdown (0 when not prompted). */
  remainingMs: number;
  /** Dismiss the prompt and reset the idle timer (user chose to stay). */
  stayActive: () => void;
};
function now(): number {
  return Date.now();
}
function readStoredActivity(): number | null {
  try {
    const raw = window.localStorage.getItem(ACTIVITY_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
}
function writeStoredActivity(timestamp: number): void {
  try {
    window.localStorage.setItem(ACTIVITY_STORAGE_KEY, String(timestamp));
  } catch {
    // Storage may be unavailable (private mode / SSR) — degrade gracefully.
  }
}

/**
 * Tracks user activity and drives an idle-session lifecycle:
 * inactivity -> keep-alive prompt with countdown -> auto sign-out.
 *
 * Activity is synced across tabs via `localStorage`, so interacting in one tab
 * keeps the session alive everywhere and a sign-out propagates to all tabs.
 */
export function useIdleTimeout({
  idleTimeoutMs,
  warningCountdownMs,
  enabled = true,
  onIdleTimeout
}: UseIdleTimeoutOptions): UseIdleTimeoutResult {
  const [isPrompted, setIsPrompted] = React.useState(false);
  const [remainingMs, setRemainingMs] = React.useState(0);
  const lastActivityRef = React.useRef<number>(now());
  const promptDeadlineRef = React.useRef<number | null>(null);
  const lastActivityWriteRef = React.useRef<number>(0);
  const hasTimedOutRef = React.useRef(false);
  // Keep latest callback without re-subscribing timers/listeners.
  const onIdleTimeoutRef = React.useRef(onIdleTimeout);
  onIdleTimeoutRef.current = onIdleTimeout;
  const registerActivity = React.useCallback((timestamp: number): void => {
    lastActivityRef.current = timestamp;
    if (timestamp - lastActivityWriteRef.current >= ACTIVITY_WRITE_THROTTLE_MS) {
      lastActivityWriteRef.current = timestamp;
      writeStoredActivity(timestamp);
    }
  }, []);
  const stayActive = React.useCallback((): void => {
    const timestamp_0 = now();
    lastActivityRef.current = timestamp_0;
    promptDeadlineRef.current = null;
    hasTimedOutRef.current = false;
    setIsPrompted(false);
    setRemainingMs(0);
    writeStoredActivity(timestamp_0);
    try {
      window.localStorage.setItem(STAY_ACTIVE_STORAGE_KEY, String(timestamp_0));
    } catch {
      // ignore storage failures
    }
  }, []);
  React.useEffect(() => {
    if (!enabled) {
      setIsPrompted(false);
      setRemainingMs(0);
      promptDeadlineRef.current = null;
      hasTimedOutRef.current = false;
      return;
    }

    // Seed from cross-tab activity so a fresh mount doesn't reset everyone.
    // Only carry the stored timestamp forward if it is recent enough that it
    // would NOT immediately trigger the idle prompt — otherwise start fresh.
    // (Stale timestamps from previous visits would otherwise pop the dialog
    // within one tick of the page loading.)
    const stored = readStoredActivity();
    const currentTime = now();
    const isStoredRecent = stored !== null && currentTime - stored < idleTimeoutMs;
    lastActivityRef.current = isStoredRecent ? stored : currentTime;
    const handleActivity = (): void => {
      // Once the prompt is shown, only an explicit choice keeps the session.
      if (promptDeadlineRef.current !== null) {
        return;
      }
      registerActivity(now());
    };
    const handleStorage = (event: StorageEvent): void => {
      if (event.key === ACTIVITY_STORAGE_KEY && event.newValue) {
        const parsed = Number(event.newValue);
        if (Number.isFinite(parsed) && parsed > lastActivityRef.current) {
          lastActivityRef.current = parsed;
        }
      }
      if (event.key === STAY_ACTIVE_STORAGE_KEY && event.newValue) {
        promptDeadlineRef.current = null;
        hasTimedOutRef.current = false;
        lastActivityRef.current = now();
        setIsPrompted(false);
        setRemainingMs(0);
      }
    };
    ACTIVITY_EVENTS.forEach(eventName => window.addEventListener(eventName, handleActivity, {
      passive: true
    }));
    window.addEventListener('storage', handleStorage);
    const interval = window.setInterval(() => {
      const current = now();
      if (promptDeadlineRef.current === null) {
        if (current - lastActivityRef.current >= idleTimeoutMs) {
          promptDeadlineRef.current = current + warningCountdownMs;
          setIsPrompted(true);
          setRemainingMs(warningCountdownMs);
        }
        return;
      }
      const remaining = promptDeadlineRef.current - current;
      if (remaining <= 0) {
        setRemainingMs(0);
        if (!hasTimedOutRef.current) {
          hasTimedOutRef.current = true;
          onIdleTimeoutRef.current();
        }
        return;
      }
      setRemainingMs(remaining);
    }, TICK_INTERVAL_MS);
    return () => {
      ACTIVITY_EVENTS.forEach(eventName_0 => window.removeEventListener(eventName_0, handleActivity));
      window.removeEventListener('storage', handleStorage);
      window.clearInterval(interval);
    };
  }, [enabled, idleTimeoutMs, warningCountdownMs, registerActivity]);
  return {
    isPrompted,
    remainingMs,
    stayActive
  };
}